import { getModuleLabelById } from "@xmiles/holopix-helper";
import { ModuleId, type ModuleIds } from "@xmiles/holopix-types";
import { V3_EXTENDED_RATIO_OPTIONS, V3_EXTENDED_RATIO_PIXELS } from "./v3RatioExtensions";

// ============ 画布模型白名单配置 ============

/**
 * 画布模型白名单 - 拖拽上传时需要携带clientId的模块
 * 这些模块生成的图片拖拽到画布时需要保留clientId用于关联
 * 包括:Nano Banner、豆包、极速编辑
 */
export const CANVAS_MODULE_WHITELIST: ModuleIds[] = [
  // V3.5 精绘（列表首位，与 MODEL_OPTIONS 展示序一致）
  ModuleId.GptImageV35,
  // V3.5 极速
  ModuleId.GptImageV35Flare,
  // V3-企业专线
  ModuleId.GptImage2Enterprise,
  // V3-普通版
  ModuleId.GptImage2,
  // 全能编辑 V2.1
  ModuleId.MonkeyV21,
  //Monkey
  ModuleId.Monkey,
  //豆包
  ModuleId.SeedDream,
  // 自由画布 Seedream V5 Pro
  ModuleId.CanvasSeedreamV5Pro,
  // Midjourney V8.1
  ModuleId.MidjourneyV81,
  // Midjourney V7
  ModuleId.Midjourney,
  // Midjourney Niji7
  ModuleId.MidjourneyNiji7,
  // Midjourney V8.2
  ModuleId.MidjourneyV82,
  // 阿里云悠船（118–121）
  ModuleId.AliyunYouchuan,
  ModuleId.AliyunYouchuanV81,
  ModuleId.AliyunYouchuanNiji7,
  ModuleId.AliyunYouchuanV82Fastest,
  //极速编辑
  ModuleId.KleinFast,

  // 自由画布-万能渲染
  ModuleId.CanvasRender,
  // 自由画布视频生成
  ModuleId.CanvasStartEndVideo,
  ModuleId.CanvasImage2Video,
  ModuleId.CanvasReferenceVideo,
  // Seedance 2.0 视频
  ModuleId.SeedanceStartEndVideo,
  ModuleId.SeedanceImage2Video,
  ModuleId.SeedanceReferenceVideo,
  ModuleId.SeedanceText2Video,
  // Seedance Mini 视频
  ModuleId.SeedanceMiniStartEndVideo,
  ModuleId.SeedanceMiniReferenceVideo,
  ModuleId.SeedanceMiniImage2Video,
  ModuleId.SeedanceMiniText2Video,
  // MiniMax H3 视频
  ModuleId.MinimaxH3StartEndVideo,
  ModuleId.MinimaxH3ReferenceVideo,
  ModuleId.MinimaxH3Image2Video,
  ModuleId.MinimaxH3Text2Video,
  ModuleId.MinimaxH3FastStartEndVideo,
  ModuleId.MinimaxH3FastReferenceVideo,
  ModuleId.MinimaxH3FastImage2Video,
  ModuleId.MinimaxH3FastText2Video,
  ModuleId.MinimaxH3MaxStartEndVideo,
  ModuleId.MinimaxH3MaxImage2Video,
  ModuleId.MinimaxH3MaxText2Video,
  // 自由画布-相似图裂变
  ModuleId.CanvasFission,

  // 自由画布-一键抠图
  ModuleId.CanvasMatting,

  // 自由画布-智能扩图
  ModuleId.VisualExpansionV2,

  // 自由画布局部修改
  ModuleId.CanvasLocalRefine,

  // 自由画布-一键成稿
  ModuleId.CanvasDraft,

  // 自由画布-一键成稿 A3（Qwen-image，纳入失败统计与切换建议）
  ModuleId.CanvasDraftA3,

  // 自由画布-自由视角
  ModuleId.CanvasQwenFreePerspective,
  // Canvas 3D generation results must keep clientId when dragged to the canvas.
  ModuleId.CanvasGenerate3d
];

/**
 * 检查模块是否在画布白名单中
 * @param moduleId 模块ID
 * @returns 是否在白名单中
 */
export function isCanvasModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return CANVAS_MODULE_WHITELIST.includes(Number(moduleId) as ModuleIds);
}

// ============ 图像质量选项 ============

export type ImageQuality = "1K" | "2K" | "4K" | "smart";

export interface QualityOption {
  value: ImageQuality;
  label: string;
  premiumBadge?: "vip" | "svip";
}

/** 全能编辑 V1 / V2 的图像质量选项 */
export const QUALITY_OPTIONS: QualityOption[] = [
  { value: "2K", label: "高清 2K" },
  { value: "4K", label: "超清 4K", premiumBadge: "svip" }
];

/** 全能编辑 V2 专用图像质量选项（支持 1K / 2K / 4K） */
export const V2_QUALITY_OPTIONS: QualityOption[] = [
  { value: "1K", label: "标清 1K" },
  { value: "2K", label: "高清 2K" },
  { value: "4K", label: "超清 4K", premiumBadge: "svip" }
];

/** 极速编辑 Lite 专用图像质量选项（仅支持 1K / 2K） */
export const LITE_QUALITY_OPTIONS: QualityOption[] = [
  { value: "1K", label: "标清 1K" },
  { value: "2K", label: "高清 2K", premiumBadge: "vip" }
];

export const DEFAULT_QUALITY: ImageQuality = "2K";

// ============ 图片尺寸/比例选项（接口 + V2 常量提前，供 ModelOption 引用） ============

export interface RatioOption {
  /** 比例值（提交给后端） */
  value: string;
  /** 显示文本 */
  label: string;
  /** 图标宽度（20×20 容器内） */
  iconWidth: number;
  /** 图标高度（20×20 容器内） */
  iconHeight: number;
  /** 是否为"智能"选项（仅部分模型支持） */
  isSmart?: boolean;
}

/**
 * 比例选项列表
 * 图标尺寸按比例缩放至 20×20 容器：
 *   - 纵向比例：高度固定 20，宽度 = 20 × (w/h)
 *   - 横向比例：宽度固定 20，高度 = 20 × (h/w)
 */
export const RATIO_OPTIONS: RatioOption[] = [
  { value: "smart", label: "智能", iconWidth: 20, iconHeight: 20, isSmart: true },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "2:3", label: "2:3", iconWidth: 13, iconHeight: 20 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:2", label: "3:2", iconWidth: 20, iconHeight: 13 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 }
];

/** 全能编辑 V2 专用比例选项（16 项） */
export const V2_RATIO_OPTIONS: RatioOption[] = [
  { value: "smart", label: "智能", iconWidth: 20, iconHeight: 20, isSmart: true },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:2", label: "3:2", iconWidth: 20, iconHeight: 13 },
  { value: "5:4", label: "5:4", iconWidth: 20, iconHeight: 16 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 },
  { value: "4:1", label: "4:1", iconWidth: 20, iconHeight: 5 },
  { value: "8:1", label: "8:1", iconWidth: 20, iconHeight: 3 },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "2:3", label: "2:3", iconWidth: 13, iconHeight: 20 },
  { value: "4:5", label: "4:5", iconWidth: 16, iconHeight: 20 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "1:4", label: "1:4", iconWidth: 5, iconHeight: 20 },
  { value: "1:8", label: "1:8", iconWidth: 3, iconHeight: 20 }
];

/**
 * 全能编辑 V3 基础比例选项（11 项）
 * 企业专线沿用基础集（其尺寸表为服务端约束，见 {@link ENTERPRISE_V3_RATIO_QUALITY_PIXELS}）；
 * V3 普通版的对外全集见 {@link V3_RATIO_OPTIONS}
 */
export const V3_BASE_RATIO_OPTIONS: RatioOption[] = [
  { value: "smart", label: "智能", iconWidth: 20, iconHeight: 20, isSmart: true },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "3:2", label: "3:2", iconWidth: 20, iconHeight: 13 },
  { value: "2:3", label: "2:3", iconWidth: 13, iconHeight: 20 },
  { value: "5:4", label: "5:4", iconWidth: 20, iconHeight: 16 },
  { value: "4:5", label: "4:5", iconWidth: 16, iconHeight: 20 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 }
];

/**
 * 全能编辑 V3 全量比例选项 = 基础集 + 扩展集（HP-9668）
 * 实际路由渠道为 kie(GPT Image 2) / RunningHub（HP-9950 收窄口径），两渠道各档位并集
 * 一致、比例×档位无空洞；后端在支持渠道间自动路由（8:5/5:8 两渠道均无，已从并集移除）
 */
export const V3_RATIO_OPTIONS: RatioOption[] = [...V3_BASE_RATIO_OPTIONS, ...V3_EXTENDED_RATIO_OPTIONS];

/**
 * 全能编辑 V3.5 比例选项（1K 全集）
 * UI 展示全集；2K/4K 通过 {@link V35_1K_ONLY_RATIOS} 禁用收窄。
 * 产品文档中的 auto 对应前端 value=`smart`（展示「智能」）。
 */
export const V35_RATIO_OPTIONS: RatioOption[] = [
  { value: "smart", label: "智能", iconWidth: 20, iconHeight: 20, isSmart: true },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "3:2", label: "3:2", iconWidth: 20, iconHeight: 13 },
  { value: "2:3", label: "2:3", iconWidth: 13, iconHeight: 20 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 },
  { value: "27:16", label: "27:16", iconWidth: 20, iconHeight: 12 },
  { value: "16:27", label: "16:27", iconWidth: 12, iconHeight: 20 },
  { value: "9:8", label: "9:8", iconWidth: 20, iconHeight: 18 },
  { value: "8:9", label: "8:9", iconWidth: 18, iconHeight: 20 }
];

/** V3.5 仅 1K 可选；2K/4K 下禁用 */
export const V35_1K_ONLY_RATIOS = ["27:16", "16:27", "9:8", "8:9"] as const;

/** 按质量返回 V3.5 可选比例项（含 smart；供智能比例匹配与禁用计算） */
export function getV35RatioOptionsForQuality(quality?: ImageQuality): RatioOption[] {
  const normalized = (quality ?? "2K").toUpperCase();
  if (normalized === "1K") return V35_RATIO_OPTIONS;
  return V35_RATIO_OPTIONS.filter(
    (item) => item.isSmart || !V35_1K_ONLY_RATIOS.includes(item.value as (typeof V35_1K_ONLY_RATIOS)[number]),
  );
}

/** Seedream V5 Pro 专用比例选项（8 个标准比例 + 智能） */
export const SEEDREAM_V5_PRO_RATIO_OPTIONS: RatioOption[] = [
  { value: "smart", label: "智能", iconWidth: 20, iconHeight: 20, isSmart: true },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:2", label: "3:2", iconWidth: 20, iconHeight: 13 },
  { value: "2:3", label: "2:3", iconWidth: 13, iconHeight: 20 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 }
];

/** Seedance 2.0 视频比例选项 */
export const SEEDANCE_RATIO_OPTIONS: RatioOption[] = [
  { value: "16:9", label: "16:9", iconWidth: 20, iconHeight: 11 },
  { value: "9:16", label: "9:16", iconWidth: 11, iconHeight: 20 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 15 },
  { value: "3:4", label: "3:4", iconWidth: 15, iconHeight: 20 },
  { value: "1:1", label: "1:1", iconWidth: 20, iconHeight: 20 },
  { value: "21:9", label: "21:9", iconWidth: 20, iconHeight: 9 },
];

/** Seedream V5 Pro 专用图像质量选项（仅支持 1K / 2K） */
export const SEEDREAM_V5_PRO_QUALITY_OPTIONS: QualityOption[] = [
  { value: "1K", label: "标清 1K" },
  { value: "2K", label: "高清 2K" }
];

export const DEFAULT_RATIO = "9:16";

/**
 * V3（GptImage2）4K 比例白名单
 * NOTE: 生成侧已随 HP-9668 全量放开（后端按渠道自动路由），当前仅扩图面板
 * （OutpaintPanel 的 OUTPAINT_4K_ALLOWED_RATIOS）复用；扩图放开时同步清理
 */
export const V3_4K_ALLOWED_RATIOS = ["16:9", "9:16", "21:9"];

// ============ 企业专线算力消耗 ============

/**
 * 全能编辑 V3 企业专线（ModuleId.GptImage2Enterprise = 46）算力消耗映射
 * 键为分辨率（ImageQuality），值为消耗算力数
 */
export const ENTERPRISE_V3_POWER_CONSUMPTION: Partial<Record<ImageQuality, number>> = {
  "1K": 65,
  "2K": 100,
  "4K": 150
};

// ============ 编辑模型选项 ============

export interface ModelOption {
  /** 模型标识（对应 holopix-types 的 ModuleIds） */
  id: ModuleIds;
  /** 模型名称 */
  name: string;
  /** 模型描述 */
  description: string;
  /** 是否支持比例选择 */
  supportsRatio: boolean;
  /** 是否支持"智能"比例选项（V1 和 Lite 支持） */
  hasSmartRatio: boolean;
  /** 模型图标 */
  icon: string;
  /** 该模型支持的图像质量选项，未指定则使用默认 QUALITY_OPTIONS */
  qualityOptions?: QualityOption[];
  /** 是否支持图像质量选择（不支持时隐藏质量 UI 且提交不传 quality） */
  supportsQuality?: boolean;
  /** 该模型支持的比例选项，未指定则使用默认 RATIO_OPTIONS */
  ratioOptions?: RatioOption[];
  /** 该模型的默认图像质量，切换到该模型时重置为此值 */
  defaultQuality?: ImageQuality;
  /** 该模型的默认比例，切换到该模型时重置为此值 */
  defaultRatio?: string;
  /** 该模型最大参考图数量，未指定则使用 {@link IMAGE_EDIT_MAX_REFERENCE_COUNT} */
  maxImages?: number;
  /** 是否展示 "New" 标识（用于新上线模型的视觉提示） */
  isNew?: boolean;
  /** 是否展示「推荐」标识 */
  isRecommended?: boolean;
  /** 是否已下架（下架模型不在选择器中展示，配置保留以兼容历史数据） */
  isDelisted?: boolean;
  /** 是否在快速编辑中隐藏（MJ 等独立生图体系模型不在快速编辑中展示） */
  hideInQuickEdit?: boolean;
}

/** 图片编辑（非 Lite）最大参考图数量；MJ 图片生成仍走独立 1 张垫图逻辑，不使用此值。 */
export const IMAGE_EDIT_MAX_REFERENCE_COUNT = 10;

/** 极速编辑 Lite 最大参考图数量。 */
export const KLEIN_FAST_MAX_REFERENCE_COUNT = 3;

export const MODEL_OPTIONS: ModelOption[] = [
  {
    id: ModuleId.GptImageV35,
    name: getModuleLabelById(ModuleId.GptImageV35)!,
    description: "最新顶配，细节天花板，连改不走样",
    supportsRatio: true,
    hasSmartRatio: true,
    supportsQuality: true,
    defaultRatio: "smart",
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    qualityOptions: V2_QUALITY_OPTIONS,
    ratioOptions: V35_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v35.png", import.meta.url).href,
    isNew: true,
  },
  {
    id: ModuleId.GptImageV35Flare,
    name: getModuleLabelById(ModuleId.GptImageV35Flare)!,
    description: "更快出图，性价比更高，适合快速迭代",
    supportsRatio: true,
    hasSmartRatio: true,
    supportsQuality: true,
    defaultRatio: "smart",
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    qualityOptions: V2_QUALITY_OPTIONS,
    ratioOptions: V35_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v35.png", import.meta.url).href,
    isRecommended: true,
  },
  {
    id: ModuleId.GptImage2Enterprise,
    name: getModuleLabelById(ModuleId.GptImage2Enterprise)!,
    description: "独享不排队通道，支持直出透明底",
    supportsRatio: true,
    hasSmartRatio: true,
    supportsQuality: true,
    defaultRatio: "smart",
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    qualityOptions: V2_QUALITY_OPTIONS,
    ratioOptions: V3_BASE_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v3.png", import.meta.url).href
  },
  {
    id: ModuleId.GptImage2,
    name: getModuleLabelById(ModuleId.GptImage2)!,
    description: "超长指令，垫图高保真，日常主力",
    supportsRatio: true,
    hasSmartRatio: true,
    supportsQuality: true,
    defaultRatio: "smart",
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    qualityOptions: V2_QUALITY_OPTIONS,
    ratioOptions: V3_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v3.png", import.meta.url).href,
  },
  {
    id: ModuleId.MonkeyV21,
    name: getModuleLabelById(ModuleId.MonkeyV21)!,
    description: "多图参考，画质高、速度快",
    supportsRatio: true,
    hasSmartRatio: true,
    defaultRatio: "smart",
    qualityOptions: V2_QUALITY_OPTIONS,
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    ratioOptions: V2_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v2.png", import.meta.url).href,
    isNew: true,
  },
  {
    id: ModuleId.Monkey,
    name: getModuleLabelById(ModuleId.Monkey)!,
    description: "多图参考，画质高、速度快",
    supportsRatio: true,
    hasSmartRatio: true,
    defaultRatio: "smart",
    qualityOptions: V2_QUALITY_OPTIONS,
    defaultQuality: "2K",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    ratioOptions: V2_RATIO_OPTIONS,
    icon: new URL("@/assets/images/board/model-v2.png", import.meta.url).href
  },
  {
    id: ModuleId.SeedDream,
    name: getModuleLabelById(ModuleId.SeedDream)!,
    description: "多角色一致性，中文海报生成，能力均衡稳定",
    supportsRatio: true,
    hasSmartRatio: true,
    defaultRatio: "smart",
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    icon: new URL("@/assets/images/board/model-v1.png", import.meta.url).href,
    isDelisted: true,
  },

  // Seedream V5 Pro
  {
    id: ModuleId.CanvasSeedreamV5Pro,
    name: "Seedream 5.0 Pro",
    description: "国产最强，审美好，画风多，中文排版稳",
    supportsRatio: true,
    hasSmartRatio: true,
    supportsQuality: true,
    defaultRatio: "smart",
    defaultQuality: "2K",
    qualityOptions: SEEDREAM_V5_PRO_QUALITY_OPTIONS,
    ratioOptions: SEEDREAM_V5_PRO_RATIO_OPTIONS,
    maxImages: IMAGE_EDIT_MAX_REFERENCE_COUNT,
    icon: new URL("@/assets/images/board/model-seedreamV5.png", import.meta.url).href,
  },
  {
    id: ModuleId.KleinFast,
    name: getModuleLabelById(ModuleId.KleinFast)!,
    description: "5 秒一张极速生，简单任务经济实惠",
    supportsRatio: true,
    hasSmartRatio: true,
    defaultRatio: "smart",
    maxImages: KLEIN_FAST_MAX_REFERENCE_COUNT,
    qualityOptions: LITE_QUALITY_OPTIONS,
    defaultQuality: "1K",
    icon: new URL("@/assets/images/board/model-lite.png", import.meta.url).href
  },
  // MJ 悠船模型（仅用于作品详情页 icon/label 展示，生图配置由 midjourneyGenerationConfig 独立管理）
  // 旧通道 66/67/68/104 + 阿里云 118–121
  {
    id: ModuleId.AliyunYouchuanV82Fastest,
    name: "Midjourney V8.2",
    description: "画面美学升级，细节与创意理解更强",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.AliyunYouchuanV81,
    name: "Midjourney V8.1",
    description: "生图更连贯，细节更丰富，美学水准大幅提升",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.AliyunYouchuan,
    name: "Midjourney V7",
    description: "最佳美学，电影质感，创意能力强",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.AliyunYouchuanNiji7,
    name: "Midjourney Niji7",
    description: "动漫审美模型，风格多样",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.MidjourneyV82,
    name: "Midjourney V8.2",
    description: "最新美学，风格更大胆，成片率高",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.MidjourneyV81,
    name: "Midjourney V8.1",
    description: "出图快且风格稳，长期量产的安全牌",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.Midjourney,
    name: "Midjourney V7",
    description: "MJ经典美学模型，构图更稳",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  },
  {
    id: ModuleId.MidjourneyNiji7,
    name: "Midjourney Niji7",
    description: "二次元专用模型，动漫画风门类最全",
    supportsRatio: false,
    hasSmartRatio: false,
    icon: new URL("@/assets/images/board/model-mj.png", import.meta.url).href,
    hideInQuickEdit: true,
  }
];

export const DEFAULT_MODEL_ID: ModuleIds = ModuleId.GptImage2;

/**
 * 后端模板 model 字段 → 前端模型 ID 映射
 * - "GPT-Image-V2.1" → 全能编辑 V2.1（显隐白名单 /api/creativeTemplate/models）
 * - "Nano banana 2" → 全能编辑 V2
 * - "Seedream 4.5"  → 全能编辑 V1
 * - "Flux2-Klein"   → 极速编辑 Lite
 */
export const BACKEND_MODEL_TO_FRONTEND_ID: Record<string, ModuleIds> = {
  "GPT-Image-V2": ModuleId.GptImage2,
  "GPT-Image-V2.1": ModuleId.MonkeyV21,
  "GPT-Image-V35": ModuleId.GptImageV35,
  "GPT-Image-V35-Flare": ModuleId.GptImageV35Flare,
  "Nano banana 2": ModuleId.Monkey,
  "Seedream 4.5": ModuleId.SeedDream,
  "Flux2-Klein": ModuleId.KleinFast,
  "GPT-Image-V2-Enterprise": ModuleId.GptImage2Enterprise,
  "Seedream-V5-Pro": ModuleId.CanvasSeedreamV5Pro
};

/**
 * 前端模型 ID → 创意模板列表接口 `model` 参数（与 {@link BACKEND_MODEL_TO_FRONTEND_ID} 的键、`CreativeTemplateResp.model` 一致）
 */
export function moduleIdToCreativeTemplateListModel(modelId: ModuleIds): string | undefined {
  const pair = Object.entries(BACKEND_MODEL_TO_FRONTEND_ID).find(([, id]) => id === modelId);
  return pair?.[0];
}

// ============ 比例 × 质量 → 像素尺寸（全能编辑 V1 提交用） ============

export interface ImagePixelSize {
  width: number;
  height: number;
}

/**
 * 各比例在 2K/4K 下的具体像素尺寸
 * 用于全能编辑 V1 在提交任务时传递 width/height
 * 「智能」比例无固定像素，不在此表中
 */
export const RATIO_QUALITY_PIXELS: Record<string, Record<string, ImagePixelSize>> = {
  "1:1": {
    "1K": { width: 1024, height: 1024 },
    "2K": { width: 2048, height: 2048 },
    "4K": { width: 4096, height: 4096 }
  },
  "4:3": {
    "1K": { width: 1152, height: 864 },
    "2K": { width: 2304, height: 1728 },
    "4K": { width: 4704, height: 3520 }
  },
  "3:4": {
    "1K": { width: 864, height: 1152 },
    "2K": { width: 1728, height: 2304 },
    "4K": { width: 3520, height: 4704 }
  },
  "16:9": {
    "1K": { width: 1024, height: 576 },
    "2K": { width: 2560, height: 1440 },
    "4K": { width: 5504, height: 3040 }
  },
  "9:16": {
    "1K": { width: 576, height: 1024 },
    "2K": { width: 1440, height: 2560 },
    "4K": { width: 3040, height: 5504 }
  },
  "3:2": {
    "1K": { width: 1024, height: 683 },
    "2K": { width: 2496, height: 1664 },
    "4K": { width: 4992, height: 3328 }
  },
  "2:3": {
    "1K": { width: 683, height: 1024 },
    "2K": { width: 1664, height: 2496 },
    "4K": { width: 3328, height: 4992 }
  },
  "5:4": {
    "1K": { width: 1024, height: 819 },
    "2K": { width: 2560, height: 2048 },
    "4K": { width: 5120, height: 4096 }
  },
  "4:5": {
    "1K": { width: 819, height: 1024 },
    "2K": { width: 2048, height: 2560 },
    "4K": { width: 4096, height: 5120 }
  },
  "21:9": {
    "1K": { width: 1024, height: 439 },
    "2K": { width: 3024, height: 1296 },
    "4K": { width: 6240, height: 2656 }
  }
};

/**
 * 根据比例与图像质量获取提交用的像素尺寸
 * 全能编辑 V1/V2 在提交任务时调用，若为「智能」比例则返回 null（不传固定像素）
 */
export function getPixelSizeForSubmit(ratio: string, quality: ImageQuality): ImagePixelSize | null {
  if (ratio === "smart") return null;
  return RATIO_QUALITY_PIXELS[ratio]?.[quality] ?? null;
}

// ============ V3 普通版 长边适配尺寸映射 ============

/**
 * 全能编辑 V3 普通版（ModuleId.GptImage2）长边适配版尺寸映射
 *
 * 规则：长边统一固定为 1K=1024 / 2K=2048 / 4K=4096，短边按原始档位实际宽高比同步缩放（四舍五入取整）。
 * 与 {@link RATIO_QUALITY_PIXELS}（V1/V2 通用表）的区别：
 *   - 通用表中部分比例的长边不等于标准长边（如 4:3 的 1K 长边为 1152，21:9 的 2K 长边为 3024）
 *   - 本表将所有长边统一收敛到 1024/2048/4096，便于输出尺寸的标准化展示
 *
 * 仅用于 V3 普通版（GptImage2），不影响 V1（SeedDream）、V2（Monkey）、V3 企业专线、极速编辑 Lite。
 */
export const V3_NORMALIZED_RATIO_QUALITY_PIXELS: Record<string, Record<string, ImagePixelSize>> = {
  "1:1": {
    "1K": { width: 1024, height: 1024 },
    "2K": { width: 2048, height: 2048 },
    "4K": { width: 4096, height: 4096 }
  },
  "4:3": {
    "1K": { width: 1024, height: 768 },
    "2K": { width: 2048, height: 1536 },
    "4K": { width: 4096, height: 3065 }
  },
  "3:4": {
    "1K": { width: 768, height: 1024 },
    "2K": { width: 1536, height: 2048 },
    "4K": { width: 3065, height: 4096 }
  },
  "16:9": {
    "1K": { width: 1024, height: 576 },
    "2K": { width: 2048, height: 1152 },
    "4K": { width: 4096, height: 2262 }
  },
  "9:16": {
    "1K": { width: 576, height: 1024 },
    "2K": { width: 1152, height: 2048 },
    "4K": { width: 2262, height: 4096 }
  },
  "3:2": {
    "1K": { width: 1024, height: 683 },
    "2K": { width: 2048, height: 1365 },
    "4K": { width: 4096, height: 2731 }
  },
  "2:3": {
    "1K": { width: 683, height: 1024 },
    "2K": { width: 1365, height: 2048 },
    "4K": { width: 2731, height: 4096 }
  },
  "5:4": {
    "1K": { width: 1024, height: 819 },
    "2K": { width: 2048, height: 1638 },
    "4K": { width: 4096, height: 3277 }
  },
  "4:5": {
    "1K": { width: 819, height: 1024 },
    "2K": { width: 1638, height: 2048 },
    "4K": { width: 3277, height: 4096 }
  },
  "21:9": {
    "1K": { width: 1024, height: 439 },
    "2K": { width: 2048, height: 878 },
    "4K": { width: 4096, height: 1743 }
  },
  // V3.5 1K 扩展比例（长边仍按 1024/2048/4096 归一；2K/4K 表项预留，UI 仅 1K 可选）
  "27:16": {
    "1K": { width: 1024, height: 607 },
    "2K": { width: 2048, height: 1214 },
    "4K": { width: 4096, height: 2427 }
  },
  "16:27": {
    "1K": { width: 607, height: 1024 },
    "2K": { width: 1214, height: 2048 },
    "4K": { width: 2427, height: 4096 }
  },
  "9:8": {
    "1K": { width: 1024, height: 910 },
    "2K": { width: 2048, height: 1820 },
    "4K": { width: 4096, height: 3641 }
  },
  "8:9": {
    "1K": { width: 910, height: 1024 },
    "2K": { width: 1820, height: 2048 },
    "4K": { width: 3641, height: 4096 }
  },
  // HP-9668 扩展比例（9:21/2:1/1:2/3:1/1:3），数据与来源见 v3RatioExtensions.ts
  ...V3_EXTENDED_RATIO_PIXELS
};

/**
 * 根据 V3 普通版比例与图像质量获取长边适配后的像素尺寸
 * 仅用于全能编辑 V3 / V3.5（GptImage2 / GptImageV35），若为「智能」比例则返回 null
 */
export function getV3NormalizedPixelSize(ratio: string, quality: ImageQuality): ImagePixelSize | null {
  if (ratio === "smart") return null;
  return V3_NORMALIZED_RATIO_QUALITY_PIXELS[ratio]?.[quality] ?? null;
}

/**
 * 根据图片宽高找到最接近的标准比例
 * 当传入 ratios 时从指定列表匹配，否则从 RATIO_QUALITY_PIXELS 的 key 匹配
 * 用于智能比例有参考图时，自动推断提交用的比例
 */
export function findClosestRatio(width: number, height: number, ratios?: RatioOption[]): string {
  const imageRatio = width / height;

  const candidates = ratios ? ratios.filter((r) => !r.isSmart).map((r) => r.value) : Object.keys(RATIO_QUALITY_PIXELS);

  let bestRatio = "1:1";
  let bestDiff = Infinity;

  for (const ratioStr of candidates) {
    const [w, h] = ratioStr.split(":").map(Number);
    const numericRatio = w / h;
    const diff = Math.abs(imageRatio - numericRatio);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestRatio = ratioStr;
    }
  }

  return bestRatio;
}

// ============ 极速编辑 Lite 比例 × 质量 → 像素尺寸 ============

/**
 * 全能编辑 V3 企业专线（ModuleId.GptImage2Enterprise）专用尺寸映射
 * 基于 16 倍数对齐与总像素范围 [655,360 ~ 8,294,400] 约束生成
 * 数据来源：服务端 GPTImage2ImageSizeConstants.java
 */
export const ENTERPRISE_V3_RATIO_QUALITY_PIXELS: Record<string, Record<string, ImagePixelSize>> = {
  "1:1": {
    "1K": { width: 1024, height: 1024 },
    "2K": { width: 2048, height: 2048 },
    "4K": { width: 2880, height: 2880 }
  },
  "4:3": {
    "1K": { width: 1024, height: 768 },
    "2K": { width: 2048, height: 1536 },
    "4K": { width: 3312, height: 2480 }
  },
  "3:4": {
    "1K": { width: 768, height: 1024 },
    "2K": { width: 1536, height: 2048 },
    "4K": { width: 2480, height: 3312 }
  },
  "3:2": {
    "1K": { width: 1024, height: 672 },
    "2K": { width: 2048, height: 1360 },
    "4K": { width: 3200, height: 2128 }
  },
  "2:3": {
    "1K": { width: 672, height: 1024 },
    "2K": { width: 1360, height: 2048 },
    "4K": { width: 2128, height: 3200 }
  },
  "5:4": {
    "1K": { width: 1024, height: 800 },
    "2K": { width: 2048, height: 1600 },
    // 修正：原 3280x2624 总像素 8,606,720 超出上限 8,294,400
    "4K": { width: 3216, height: 2576 }
  },
  "4:5": {
    "1K": { width: 800, height: 1024 },
    "2K": { width: 1600, height: 2048 },
    // 修正：原 2624x3280 总像素 8,606,720 超出上限 8,294,400
    "4K": { width: 2576, height: 3216 }
  },
  "16:9": {
    // 修正：原 1024x576 总像素 589,824 低于下限 655,360
    "1K": { width: 1088, height: 608 },
    "2K": { width: 2048, height: 1152 },
    "4K": { width: 3840, height: 2160 }
  },
  "9:16": {
    // 修正：原 576x1024 总像素 589,824 低于下限 655,360
    "1K": { width: 608, height: 1088 },
    "2K": { width: 1152, height: 2048 },
    "4K": { width: 2160, height: 3840 }
  },
  "21:9": {
    // 1K 21:9 新增：总像素 774,144，符合约束
    "1K": { width: 1344, height: 576 },
    "2K": { width: 2048, height: 864 },
    "4K": { width: 3840, height: 1632 }
  }
};

/**
 * 根据 V3 企业专线比例与图像质量获取像素尺寸
 * 若为「智能」比例则返回 null
 */
export function getEnterpriseV3PixelSize(ratio: string, quality: ImageQuality): ImagePixelSize | null {
  if (ratio === "smart") return null;
  return ENTERPRISE_V3_RATIO_QUALITY_PIXELS[ratio]?.[quality] ?? null;
}

/**
 * 极速编辑 Lite 各比例在 1K/2K 下的具体像素尺寸
 * 用于 Lite 消息展示及提交时传递 width/height
 * 「智能」比例无固定像素，不在此表中
 */
export const LITE_RATIO_QUALITY_PIXELS: Record<string, Record<string, ImagePixelSize>> = {
  "1:1": {
    "1K": { width: 1024, height: 1024 },
    "2K": { width: 1536, height: 1536 }
  },
  "4:3": {
    "1K": { width: 1024, height: 768 },
    "2K": { width: 1536, height: 1152 }
  },
  "3:4": {
    "1K": { width: 768, height: 1024 },
    "2K": { width: 1152, height: 1536 }
  },
  "16:9": {
    "1K": { width: 1024, height: 576 },
    "2K": { width: 1536, height: 864 }
  },
  "9:16": {
    "1K": { width: 576, height: 1024 },
    "2K": { width: 864, height: 1536 }
  },
  "3:2": {
    "1K": { width: 1024, height: 683 },
    "2K": { width: 1536, height: 1024 }
  },
  "2:3": {
    "1K": { width: 683, height: 1024 },
    "2K": { width: 1024, height: 1536 }
  },
  "21:9": {
    "1K": { width: 1024, height: 439 },
    "2K": { width: 1536, height: 658 }
  }
};

/**
 * 根据比例与图像质量获取 Lite 的像素尺寸
 * 若为「智能」比例则返回 null
 */
export function getLitePixelSize(ratio: string, quality: ImageQuality): ImagePixelSize | null {
  if (ratio === "smart") return null;
  return LITE_RATIO_QUALITY_PIXELS[ratio]?.[quality] ?? null;
}

// ============ Seedream V5 Pro 比例 × 质量 → 像素尺寸 ============

/**
 * Seedream V5 Pro（id=74）各比例在 1K/2K 下的具体像素尺寸
 * 数据来源：火山方舟 Seedream 5.0 Pro 官方比例尺寸表
 * 仅支持 1K 和 2K 两档分辨率
 */
export const SEEDREAM_V5_PRO_RATIO_QUALITY_PIXELS: Record<string, Record<string, ImagePixelSize>> = {
  "1:1": {
    "1K": { width: 1024, height: 1024 },
    "2K": { width: 2048, height: 2048 }
  },
  "9:16": {
    "1K": { width: 736, height: 1312 },
    "2K": { width: 1440, height: 2560 }
  },
  "16:9": {
    "1K": { width: 1312, height: 736 },
    "2K": { width: 2560, height: 1440 }
  },
  "3:4": {
    "1K": { width: 864, height: 1152 },
    "2K": { width: 1728, height: 2304 }
  },
  "4:3": {
    "1K": { width: 1152, height: 864 },
    "2K": { width: 2304, height: 1728 }
  },
  "3:2": {
    "1K": { width: 1248, height: 832 },
    "2K": { width: 2496, height: 1664 }
  },
  "2:3": {
    "1K": { width: 832, height: 1248 },
    "2K": { width: 1664, height: 2496 }
  },
  "21:9": {
    "1K": { width: 1568, height: 672 },
    "2K": { width: 3024, height: 1296 }
  }
};

/**
 * 根据 Seedream V5 Pro 比例与图像质量获取像素尺寸
 * 若为「智能」比例则返回 null
 */
export function getSeedreamV5ProPixelSize(ratio: string, quality: ImageQuality): ImagePixelSize | null {
  if (ratio === "smart") return null;
  return SEEDREAM_V5_PRO_RATIO_QUALITY_PIXELS[ratio]?.[quality] ?? null;
}

// ============ 极速编辑 Lite 专用参数映射 ============

/**
 * 比例值 → Lite 后台 size 整数（0-8）
 * 0=智能, 1=1:1, 2=4:3, 3=3:4, 4=16:9, 5=9:16, 6=3:2, 7=2:3, 8=21:9
 */
export const LITE_RATIO_SIZE_MAP: Record<string, number> = {
  smart: 0,
  "1:1": 1,
  "4:3": 2,
  "3:4": 3,
  "16:9": 4,
  "9:16": 5,
  "3:2": 6,
  "2:3": 7,
  "21:9": 8
};

/**
 * Lite 图像质量 → aspectRatios 参数值
 * 1K => '1024', 2K => '1536'
 */
export const LITE_QUALITY_ASPECT_RATIOS: Record<string, string> = {
  "1K": "1024",
  "2K": "1536"
};

/**
 * 反向映射：Lite 后台 size 整数 → 比例字符串
 * 用于 transformer 从存储的整数 size 还原出实际比例
 */
export const LITE_SIZE_RATIO_MAP: Record<number, string> = Object.fromEntries(
  Object.entries(LITE_RATIO_SIZE_MAP).map(([ratio, size]) => [size, ratio])
);

/**
 * 反向映射：Lite aspectRatios 参数值 → 图像质量
 * 用于 transformer 从存储的 '1024'/'1536' 还原出 1K/2K
 */
export const LITE_ASPECT_RATIOS_QUALITY: Record<string, ImageQuality> = Object.fromEntries(
  Object.entries(LITE_QUALITY_ASPECT_RATIOS).map(([quality, ar]) => [ar, quality as ImageQuality])
);

/**
 * 极速编辑 Lite 输入图数量 → styleId
 * 0张图（纯文生图）=> 0, 1张图 => 1, 2张图 => 2, 3张图 => 3
 */
export const LITE_IMAGE_COUNT_STYLE_ID_MAP: Record<number, number> = {
  0: 0,
  1: 1,
  2: 2,
  3: 3
};

/**
 * 根据输入图数量获取 Lite 的 styleId
 * 超过 3 张时按 3 张处理
 */
export function getLiteStyleId(imageCount: number): number {
  const count = Math.min(imageCount, 3);
  return LITE_IMAGE_COUNT_STYLE_ID_MAP[count] ?? LITE_IMAGE_COUNT_STYLE_ID_MAP[0];
}
